import { changeState, checkNextApprover, getStyle } from "./DocumentPartTreeUtils";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { UserDocumentCheckout } from "../../api/models/user-document-checkout";
import { TreeViewDataItem } from "./DocumentPartTree";
import { IDocumentPartProps } from "./IDocumentPartProps";
import {
  fetchApproveWorkflowForDirectChildren,
  fetchDocumentPartNodeWithPermissions,
  fetchDocumentPartStatus,
  fetchRetrieveComment,
  fetchSubmitWorkflowForDirectChildren,
} from "../../api/document/DocumentsPart";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { WorkflowStatus } from "../../api/models/workflow-status";
import { DocumentPartWithSuperUser } from "../../api/models/document-part-with-super-user";
import { DocumentPartWithStatus } from "../../api/models/document-part-with-status";
import { resolveWorkflowActionUser } from "./workflowActionUser";
import { hasApproveAction, hasSubmitAction, sortWorkflowActions } from "./workflowActions";

const getMessageWithFallback = (messageId: string, fallback: string) => {
  const message = formatMessage(messageId);
  return message === messageId ? fallback : message;
};

const isTreeWorkflowChild = (child: TreeViewDataItem | undefined) => child && ["part", "root"].includes(child.nodeType);

const resolveDocumentId = (value: any): string | undefined => value?.documentPart?.id ?? value?.id;

const getDirectChildrenIdsFromItem = (item: TreeViewDataItem): string[] => {
  const childIdsFromTree = (item.items ?? [])
    .filter((child): child is TreeViewDataItem => isTreeWorkflowChild(child))
    .map((child) => child.key);
  const childIdsFromData = ((item.data?.subparts ?? item.data?.documentPart?.subparts ?? []) as any[])
    .map((child) => resolveDocumentId(child))
    .filter((childId): childId is string => Boolean(childId));

  return Array.from(new Set([...childIdsFromTree, ...childIdsFromData].filter((childId) => childId !== item.key)));
};

const hasChildren = (item: TreeViewDataItem): boolean =>
  getDirectChildrenIdsFromItem(item).length > 0 ||
  Boolean(item.hasChildren || item.data?.hasChildren || item.data?.documentPart?.hasChildren);

const mergeDocumentStatuses = (
  previousState: DocumentPartWithStatus[] = [],
  nextState: DocumentPartWithStatus[] = []
): DocumentPartWithStatus[] => {
  const workflowStatusMap = new Map<string, DocumentPartWithStatus>();
  previousState.forEach((status) => workflowStatusMap.set(status.documentPartId, status));
  nextState.forEach((status) => workflowStatusMap.set(status.documentPartId, status));
  return Array.from(workflowStatusMap.values());
};

const hasWorkflowAction = (workflowStatus: WorkflowStatus | undefined, action: "SUBMIT" | "APPROVE") => {
  if (!workflowStatus?.actions?.length) {
    return false;
  }

  return action === "SUBMIT" ? hasSubmitAction(workflowStatus.actions) : hasApproveAction(workflowStatus.actions);
};

const isSignificantWorkflowStatus = (workflowStatus: WorkflowStatus | undefined): boolean =>
  Boolean(workflowStatus && (workflowStatus.isSignificant || workflowStatus.significant));

const getWorkflowStatusById = (documentPartId: string, statuses: DocumentPartWithStatus[] = []) =>
  statuses.find((status) => status.documentPartId === documentPartId)?.workflowStatus;

const loadAllDirectChildrenIds = async (documentPartId: string): Promise<string[]> => {
  const directChildrenIds: string[] = [];
  let page = 0;
  let totalPages = 1;

  while (page < totalPages) {
    const response = await fetchDocumentPartNodeWithPermissions(documentPartId, page, {
      params: { notShowLoading: true },
    });
    const currentIds = (response?._embedded?.content ?? [])
      .map((child) => child?.documentPart?.id)
      .filter((childId): childId is string => Boolean(childId));

    directChildrenIds.push(...currentIds);
    totalPages = Math.max(response?.page?.totalPages ?? 0, 1);
    page += 1;
  }

  return Array.from(new Set(directChildrenIds));
};

interface InlineActionsPanelProps {
  documentPartProps: React.PropsWithChildren<IDocumentPartProps>;
  setDisableActionWorkflow: React.Dispatch<React.SetStateAction<boolean>>;
  inlineActions: string[];
  allUserDocumentCheckouts: UserDocumentCheckout[];
  userDocumentCheckouts: UserDocumentCheckout[];
  superUser: Map<string, DocumentPartWithSuperUser>;
  workflowStatus: WorkflowStatus;
  item: TreeViewDataItem;
  rootId: string;
  setCheckoutDocument: (item: TreeViewDataItem) => void;
  setCheckinDocument: (item: TreeViewDataItem) => void;
  preloadedDirectChildrenIds?: string[];
  preloadedDirectChildrenStatuses?: DocumentPartWithStatus[];
}

const Separator = () => (
  <div
    style={{
      height: "1px",
      backgroundColor: "#e0e0e0",
      margin: "6px 0 6px -16px",
      width: "calc(100% + 32px)",
    }}
  />
);

export const InlineActionsPanel = (props: InlineActionsPanelProps) => {
  const [comment, setComment] = useState<string>();
  const [gotIt, setGotIt] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchRetrieveComment(props.item.key)
      .then(setComment)
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) return <Spinner size={SpinnerSize.large} id="spinner" />;

  return <AddCommentPanel {...props} comment={comment} gotIt={gotIt} setGotIt={setGotIt} />;
};

const AddCommentPanel = ({
  inlineActions,
  allUserDocumentCheckouts,
  userDocumentCheckouts,
  superUser,
  workflowStatus,
  item,
  rootId,
  setCheckoutDocument,
  setCheckinDocument,
  setDisableActionWorkflow,
  documentPartProps,
  preloadedDirectChildrenIds,
  preloadedDirectChildrenStatuses,
  comment,
  gotIt,
  setGotIt,
}: InlineActionsPanelProps & {
  comment?: string;
  gotIt: boolean;
  setGotIt: (v: boolean) => void;
}) => {
  const commentRef = useRef<HTMLTextAreaElement>(null);
  const [directChildrenIds, setDirectChildrenIds] = useState<string[]>(() => {
    if (preloadedDirectChildrenIds?.length) {
      return Array.from(new Set(preloadedDirectChildrenIds));
    }
    return getDirectChildrenIdsFromItem(item);
  });
  const [localDirectChildrenStatuses, setLocalDirectChildrenStatuses] = useState<DocumentPartWithStatus[]>(
    () => preloadedDirectChildrenStatuses ?? []
  );
  const [isLoadingDirectChildren, setIsLoadingDirectChildren] = useState<boolean>(false);
  const [isLoadingDirectChildrenStatuses, setIsLoadingDirectChildrenStatuses] = useState<boolean>(false);
  const [bulkActionInProgress, setBulkActionInProgress] = useState<"submit" | "approve" | null>(null);

  const isAnyDocumentCheckoutForAllUsers = allUserDocumentCheckouts?.some(
    (checkout) => checkout.documentId === item.key
  );

  const isSuperUser = superUser.get(item.key)?.isSuperUser;
  const element = workflowStatus?.actions;

  const isDocumentCheckoutForCurrentUser =
    userDocumentCheckouts?.length > 0 && userDocumentCheckouts[0]?.documentId === item.key;

  const otherDocumentCheckoutForCurrentUser =
    userDocumentCheckouts?.length > 0 && userDocumentCheckouts[0]?.documentId !== item.key;

  const isAnyDocumentCheckoutForAnyUsers = allUserDocumentCheckouts?.some(
    (checkout) => checkout.documentId === item.key
  );

  const showEnableEditSection = isSuperUser && checkNextApprover(element);
  const showDirectChildrenWorkflowSection = hasChildren(item);

  const directChildrenWorkflowStatuses = useMemo(
    () => mergeDocumentStatuses(localDirectChildrenStatuses, documentPartProps.documentPartWithStatus),
    [localDirectChildrenStatuses, documentPartProps.documentPartWithStatus]
  );

  useEffect(() => {
    setLocalDirectChildrenStatuses(preloadedDirectChildrenStatuses ?? []);
  }, [item.key, preloadedDirectChildrenStatuses]);

  useEffect(() => {
    let isCancelled = false;
    const resolvedDirectChildrenIds = getDirectChildrenIdsFromItem(item);

    if (!showDirectChildrenWorkflowSection) {
      setDirectChildrenIds([]);
      return;
    }

    if (resolvedDirectChildrenIds.length > 0) {
      setDirectChildrenIds(resolvedDirectChildrenIds);
      return;
    }

    if (preloadedDirectChildrenIds?.length) {
      setDirectChildrenIds(Array.from(new Set(preloadedDirectChildrenIds)));
      return;
    }

    setIsLoadingDirectChildren(true);
    loadAllDirectChildrenIds(item.key)
      .then((childrenIds) => {
        if (!isCancelled) {
          setDirectChildrenIds(childrenIds);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingDirectChildren(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [item, preloadedDirectChildrenIds, showDirectChildrenWorkflowSection]);

  useEffect(() => {
    let isCancelled = false;

    if (!showDirectChildrenWorkflowSection || directChildrenIds.length === 0) {
      return;
    }

    const missingStatuses = directChildrenIds.filter(
      (childId) => !getWorkflowStatusById(childId, directChildrenWorkflowStatuses)
    );

    if (missingStatuses.length === 0) {
      return;
    }

    setIsLoadingDirectChildrenStatuses(true);
    fetchDocumentPartStatus(rootId, missingStatuses)
      .then((statuses) => {
        if (!isCancelled && statuses?.length) {
          setLocalDirectChildrenStatuses((prevState) => mergeDocumentStatuses(prevState, statuses));
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingDirectChildrenStatuses(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    directChildrenIds,
    directChildrenWorkflowStatuses,
    documentPartProps.setDocumentPartWithStatus,
    rootId,
    showDirectChildrenWorkflowSection,
  ]);

  const checkedOutChildren = useMemo(
    () => new Set((allUserDocumentCheckouts ?? []).map((checkout) => checkout.documentId)),
    [allUserDocumentCheckouts]
  );

  const actionableDirectChildren = useMemo(
    () =>
      directChildrenIds
        .map((childId) => ({
          childId,
          workflowStatus: getWorkflowStatusById(childId, directChildrenWorkflowStatuses),
        }))
        .filter(({ workflowStatus }) => isSignificantWorkflowStatus(workflowStatus)),
    [directChildrenIds, directChildrenWorkflowStatuses]
  );

  const submitAllEnabled = actionableDirectChildren.some(
    ({ childId, workflowStatus }) => !checkedOutChildren.has(childId) && hasWorkflowAction(workflowStatus, "SUBMIT")
  );
  const approveAllEnabled = actionableDirectChildren.some(
    ({ childId, workflowStatus }) => !checkedOutChildren.has(childId) && hasWorkflowAction(workflowStatus, "APPROVE")
  );
  const areDirectChildrenActionsLoading = isLoadingDirectChildren || isLoadingDirectChildrenStatuses;
  const showAdvancedActions = showEnableEditSection || showDirectChildrenWorkflowSection;

  const advancedActionLabel = getMessageWithFallback("cof.base.advanced.actions", "Advanced actions");
  const submitAllLabel = getMessageWithFallback("cof.base.submit.all", "Submit all");
  const approveAllLabel = getMessageWithFallback("cof.base.approve.all", "Approve all");

  const executeDirectChildrenWorkflowAction = async (action: "submit" | "approve") => {
    if (bulkActionInProgress || directChildrenIds.length === 0) {
      return;
    }

    const executeAction =
      action === "submit" ? fetchSubmitWorkflowForDirectChildren : fetchApproveWorkflowForDirectChildren;
    const targetChildrenIds = actionableDirectChildren
      .filter(
        ({ childId, workflowStatus }) =>
          !checkedOutChildren.has(childId) &&
          hasWorkflowAction(workflowStatus, action === "submit" ? "SUBMIT" : "APPROVE")
      )
      .map(({ childId }) => childId);

    if (targetChildrenIds.length === 0) {
      return;
    }

    const involvedDocumentPartIds = Array.from(new Set(targetChildrenIds));
    const documentPartIdsToRefresh = Array.from(new Set([item.key, ...involvedDocumentPartIds]));
    const previousWorkflowStatuses = new Map<string, WorkflowStatus>();

    const setLoadingForInvolvedNodes = () => {
      if (!documentPartProps.setDocumentPartWithStatus) {
        return;
      }

      setLocalDirectChildrenStatuses((prevState) =>
        prevState.map((statusItem) => {
          if (!involvedDocumentPartIds.includes(statusItem.documentPartId)) {
            return statusItem;
          }

          previousWorkflowStatuses.set(statusItem.documentPartId, statusItem.workflowStatus);
          return { ...statusItem, workflowStatus: null as any };
        })
      );

      documentPartProps.setDocumentPartWithStatus((prevState) =>
        prevState.map((statusItem) => {
          if (!involvedDocumentPartIds.includes(statusItem.documentPartId)) {
            return statusItem;
          }

          previousWorkflowStatuses.set(statusItem.documentPartId, statusItem.workflowStatus);
          return { ...statusItem, workflowStatus: null as any };
        })
      );
    };

    const restoreWorkflowStatusesByIds = (documentPartIds: string[]) => {
      if (previousWorkflowStatuses.size === 0 || documentPartIds.length === 0) {
        return;
      }

      setLocalDirectChildrenStatuses((prevState) =>
        prevState.map((statusItem) => {
          if (!documentPartIds.includes(statusItem.documentPartId)) {
            return statusItem;
          }

          const previousStatus = previousWorkflowStatuses.get(statusItem.documentPartId);
          return previousStatus ? { ...statusItem, workflowStatus: previousStatus } : statusItem;
        })
      );

      if (documentPartProps.setDocumentPartWithStatus) {
        documentPartProps.setDocumentPartWithStatus((prevState) =>
          prevState.map((statusItem) => {
            if (!documentPartIds.includes(statusItem.documentPartId)) {
              return statusItem;
            }

            const previousStatus = previousWorkflowStatuses.get(statusItem.documentPartId);
            return previousStatus ? { ...statusItem, workflowStatus: previousStatus } : statusItem;
          })
        );
      }
    };

    const restoreWorkflowStatusesForInvolvedNodes = () => {
      restoreWorkflowStatusesByIds(involvedDocumentPartIds);
    };

    setDisableActionWorkflow(true);
    setBulkActionInProgress(action);
    setLoadingForInvolvedNodes();

    try {
      const workflowActionUser = resolveWorkflowActionUser();
      const changedStatuses =
        (await (workflowActionUser
          ? executeAction(rootId, item.key, commentRef.current?.value, workflowActionUser)
          : executeAction(rootId, item.key, commentRef.current?.value))) ?? [];
      const refreshedStatuses = (await fetchDocumentPartStatus(rootId, documentPartIdsToRefresh)) ?? [];
      const workflowStatusesToMerge = mergeDocumentStatuses(refreshedStatuses, changedStatuses);

      if (workflowStatusesToMerge?.length) {
        setLocalDirectChildrenStatuses((prevState) => mergeDocumentStatuses(prevState, workflowStatusesToMerge));
        if (documentPartProps.setDocumentPartWithStatus) {
          documentPartProps.setDocumentPartWithStatus((prevState) =>
            mergeDocumentStatuses(prevState, workflowStatusesToMerge)
          );
        }
      }

      const updatedIds = new Set(workflowStatusesToMerge.map((status) => status.documentPartId));
      const unresolvedIds = involvedDocumentPartIds.filter((documentPartId) => !updatedIds.has(documentPartId));
      if (unresolvedIds.length > 0) {
        restoreWorkflowStatusesByIds(unresolvedIds);
      }

      localStorage.removeItem(`comment-${item.key}`);
    } catch {
      try {
        const currentStatuses = await fetchDocumentPartStatus(rootId, documentPartIdsToRefresh);
        if (currentStatuses?.length) {
          setLocalDirectChildrenStatuses((prevState) => mergeDocumentStatuses(prevState, currentStatuses));
          if (documentPartProps.setDocumentPartWithStatus) {
            documentPartProps.setDocumentPartWithStatus((prevState) =>
              mergeDocumentStatuses(prevState, currentStatuses)
            );
          }
        } else {
          restoreWorkflowStatusesForInvolvedNodes();
        }
      } catch {
        restoreWorkflowStatusesForInvolvedNodes();
      }
    } finally {
      setBulkActionInProgress(null);
      setDisableActionWorkflow(false);
    }
  };

  const availableInlineActions = sortWorkflowActions(inlineActions);
  // The comment button sits right after the first action; every remaining action follows it, so a
  // user holding more than two workflow permissions still sees all of them (IDS-7322).
  const actionsWithComment =
    availableInlineActions.length > 0 ? [availableInlineActions[0], "comment", ...availableInlineActions.slice(1)] : [];

  const hasGotItAction = Boolean(comment && !gotIt);
  const hasEnableEditActions =
    showEnableEditSection && (!isAnyDocumentCheckoutForAnyUsers || isDocumentCheckoutForCurrentUser);
  const hasBulkWorkflowActions = submitAllEnabled || approveAllEnabled;
  const hasCurrentNodeWorkflowActions = availableInlineActions.length > 0;
  const shouldRenderPanel =
    hasCurrentNodeWorkflowActions ||
    hasEnableEditActions ||
    hasBulkWorkflowActions ||
    hasGotItAction ||
    areDirectChildrenActionsLoading;

  if (!shouldRenderPanel) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "0px 16px 6px 16px",
        width: "100%",
        fontSize: "0.85rem",
        color: "#000",
      }}
    >
      {comment && !gotIt ? (
        <textarea
          key="readonly"
          readOnly
          value={comment}
          style={{
            width: "100%",
            resize: "vertical",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontSize: "0.85rem",
            background: "#f0f0f0",
            color: "#666",
            marginTop: "8px",
            marginBottom: "8px",
            overflow: "auto",
          }}
        />
      ) : (
        !isAnyDocumentCheckoutForAllUsers && (
          <textarea
            key="edit"
            ref={commentRef}
            rows={2}
            defaultValue={localStorage.getItem(`comment-${item.key}`) ?? ""}
            placeholder={formatMessage("cof.base.insert.comment")}
            onInput={() => {
              if (commentRef.current) {
                commentRef.current.style.height = "auto";
                commentRef.current.style.height = commentRef.current.scrollHeight + "px";
                localStorage.setItem(`comment-${item.key}`, commentRef.current?.value);
              }
            }}
            style={{
              width: "100%",
              resize: "vertical",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontSize: "0.85rem",
              background: "#fff",
              color: "#000",
              marginTop: "8px",
              marginBottom: "8px",
              overflow: "auto",
            }}
          />
        )
      )}
      <Separator />
      <div
        style={{
          display: "flex",
          justifyContent: actionsWithComment.length === 1 ? "center" : "space-between",
          gap: "16px",
        }}
      >
        {actionsWithComment.map((action) => {
          const disabled = isAnyDocumentCheckoutForAllUsers;
          if (action === "comment") {
            return (
              <button
                key="comment"
                disabled={comment === "" || gotIt}
                onClick={() => {
                  setGotIt(true);
                }}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  margin: 0,
                  cursor: comment === "" || gotIt ? "not-allowed" : "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                  color: comment === "" || gotIt ? "#999" : "#0078d4",
                  opacity: comment === "" || gotIt ? 0.5 : 1,
                }}
              >
                {formatMessage("cof.base.gotIt")}
              </button>
            );
          }
          const style = getStyle(action, disabled);
          return (
            <button
              key={action}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                margin: 0,
                cursor: disabled ? "not-allowed" : "pointer",
                fontSize: "0.9rem",
                fontWeight: "bold",
                color: style.color,
                opacity: disabled ? 0.5 : 1,
                userSelect: "none",
              }}
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  changeState(
                    item,
                    action,
                    setDisableActionWorkflow,
                    documentPartProps,
                    rootId,
                    commentRef.current?.value
                  );
                }
              }}
            >
              {(() => {
                let actionLabel = action?.toLowerCase().trim();
                if (actionLabel?.startsWith("approve")) actionLabel = "approve";
                if (actionLabel?.startsWith("reject")) actionLabel = "reject";
                return actionLabel ? formatMessage("cof.base." + actionLabel) : "";
              })()}
            </button>
          );
        })}
      </div>
      {showAdvancedActions && (
        <>
          <Separator />
          <div
            style={{
              fontSize: "0.95rem",
              color: "#222",
              marginBottom: "4px",
            }}
          >
            {advancedActionLabel}
          </div>
          {showEnableEditSection && (
            <>
              {otherDocumentCheckoutForCurrentUser && !isAnyDocumentCheckoutForAnyUsers && (
                <div
                  style={{
                    fontSize: "0.750rem",
                    marginTop: 0,
                    marginBottom: "8px",
                    padding: "6px",
                    backgroundColor: "#EA8F00",
                    color: "black",
                    fontWeight: "bold",
                    borderRadius: "4px",
                  }}
                >
                  {formatMessage("cof.base.enable.edit.notpossible")}
                </div>
              )}

              {(!isAnyDocumentCheckoutForAnyUsers || isDocumentCheckoutForCurrentUser) && (
                <div>
                  <div
                    style={{
                      fontSize: "0.78rem",
                      fontWeight: "600",
                      color: "#666",
                      marginTop: "2px",
                      marginBottom: "4px",
                    }}
                  >
                    {formatMessage("cof.base.edit.certified.data")}
                  </div>

                  {!isAnyDocumentCheckoutForAnyUsers && (
                    <div
                      role="button"
                      tabIndex={0}
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        cursor: "pointer",
                        color: "#0078d4",
                        marginTop: "7px",
                      }}
                      onClick={() => setCheckoutDocument(item)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") setCheckoutDocument(item);
                      }}
                    >
                      {formatMessage("cof.base.enable.edit")}
                    </div>
                  )}

                  {isDocumentCheckoutForCurrentUser && (
                    <div
                      role="button"
                      tabIndex={0}
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        cursor: "pointer",
                        color: "#0078d4",
                        marginTop: "12px",
                      }}
                      onClick={() => setCheckinDocument(item)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") setCheckinDocument(item);
                      }}
                    >
                      {formatMessage("cof.base.disable.edit")}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {showDirectChildrenWorkflowSection && (
            <div style={{ marginTop: showEnableEditSection ? "8px" : 0 }}>
              <div
                role="button"
                tabIndex={submitAllEnabled && !areDirectChildrenActionsLoading && !bulkActionInProgress ? 0 : -1}
                style={{
                  fontSize: "0.85rem",
                  fontWeight: "bold",
                  cursor:
                    submitAllEnabled && !areDirectChildrenActionsLoading && !bulkActionInProgress
                      ? "pointer"
                      : "not-allowed",
                  color:
                    submitAllEnabled && !areDirectChildrenActionsLoading && !bulkActionInProgress
                      ? "#0078d4"
                      : "#b7b7b7",
                  marginTop: "4px",
                }}
                onClick={() => {
                  if (submitAllEnabled && !areDirectChildrenActionsLoading && !bulkActionInProgress) {
                    void executeDirectChildrenWorkflowAction("submit");
                  }
                }}
                onKeyDown={(e) => {
                  if (
                    (e.key === "Enter" || e.key === " ") &&
                    submitAllEnabled &&
                    !areDirectChildrenActionsLoading &&
                    !bulkActionInProgress
                  ) {
                    void executeDirectChildrenWorkflowAction("submit");
                  }
                }}
              >
                {submitAllLabel}
              </div>
              <div
                role="button"
                tabIndex={approveAllEnabled && !areDirectChildrenActionsLoading && !bulkActionInProgress ? 0 : -1}
                style={{
                  fontSize: "0.85rem",
                  fontWeight: "bold",
                  cursor:
                    approveAllEnabled && !areDirectChildrenActionsLoading && !bulkActionInProgress
                      ? "pointer"
                      : "not-allowed",
                  color:
                    approveAllEnabled && !areDirectChildrenActionsLoading && !bulkActionInProgress
                      ? "#0078d4"
                      : "#b7b7b7",
                  marginTop: "6px",
                }}
                onClick={() => {
                  if (approveAllEnabled && !areDirectChildrenActionsLoading && !bulkActionInProgress) {
                    void executeDirectChildrenWorkflowAction("approve");
                  }
                }}
                onKeyDown={(e) => {
                  if (
                    (e.key === "Enter" || e.key === " ") &&
                    approveAllEnabled &&
                    !areDirectChildrenActionsLoading &&
                    !bulkActionInProgress
                  ) {
                    void executeDirectChildrenWorkflowAction("approve");
                  }
                }}
              >
                {approveAllLabel}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
